import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastContainer, ToastData, ToastType, ToastDetail } from '../components/common/Toast/Toast';

interface ToastOptions {
  title: string;
  message?: string;
  details?: ToastDetail[];
  duration?: number;
}

interface ToastContextValue {
  showToast: (type: ToastType, options: ToastOptions) => void;
  success: (title: string, message?: string, details?: ToastDetail[]) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((type: ToastType, options: ToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    setToasts(prev => [...prev, { id, type, ...options }]);
  }, []);

  const success = useCallback((title: string, message?: string, details?: ToastDetail[]) => {
    showToast('success', { title, message, details });
  }, [showToast]);

  const error = useCallback((title: string, message?: string) => {
    showToast('error', { title, message, duration: 8000 });
  }, [showToast]);

  const warning = useCallback((title: string, message?: string) => {
    showToast('warning', { title, message });
  }, [showToast]);

  const info = useCallback((title: string, message?: string) => {
    showToast('info', { title, message });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};
